import * as React from "react";
import {
    GetVisualViewportScale,
    GetWindowScrollPosition,
    IsIosDevice,
    RestoreWindowScrollPosition,
} from "../View/QuickJournalLayout";

export function useQuickJournalViewportController() {
    const [visualViewportScale, setVisualViewportScale] = React.useState(
        GetVisualViewportScale()
    );
    const windowScrollPositionOnOpen = React.useRef(GetWindowScrollPosition());
    const scrollRestoreTimeoutIds = React.useRef<number[]>([]);
    const pageWasHidden = React.useRef(false);

    React.useEffect(() => {
        if (!IsIosDevice()) {
            return;
        }

        const scheduleScrollRestoreAfterUnlock = () => {
            if (!pageWasHidden.current) {
                return;
            }

            pageWasHidden.current = false;
            RestoreWindowScrollPosition(windowScrollPositionOnOpen.current);
            scrollRestoreTimeoutIds.current.forEach((timeoutId) =>
                window.clearTimeout(timeoutId)
            );
            scrollRestoreTimeoutIds.current = [80, 250, 600, 1000].map((delay) =>
                window.setTimeout(
                    () =>
                        RestoreWindowScrollPosition(
                            windowScrollPositionOnOpen.current
                        ),
                    delay
                )
            );
        };

        const handleVisibilityChange = () => {
            if (document.visibilityState === "hidden") {
                pageWasHidden.current = true;
                return;
            }

            scheduleScrollRestoreAfterUnlock();
        };

        window.addEventListener("focus", scheduleScrollRestoreAfterUnlock);
        window.addEventListener("pageshow", scheduleScrollRestoreAfterUnlock);
        document.addEventListener("visibilitychange", handleVisibilityChange);

        return () => {
            window.removeEventListener("focus", scheduleScrollRestoreAfterUnlock);
            window.removeEventListener("pageshow", scheduleScrollRestoreAfterUnlock);
            document.removeEventListener(
                "visibilitychange",
                handleVisibilityChange
            );
            scrollRestoreTimeoutIds.current.forEach((timeoutId) =>
                window.clearTimeout(timeoutId)
            );
        };
    }, []);

    React.useEffect(() => {
        const handleViewportChange = () => {
            const nextScale = GetVisualViewportScale();

            setVisualViewportScale((currentScale) =>
                Math.abs(currentScale - nextScale) < 0.01
                    ? currentScale
                    : nextScale
            );
        };

        handleViewportChange();
        window.visualViewport?.addEventListener("resize", handleViewportChange);
        window.visualViewport?.addEventListener("scroll", handleViewportChange);

        return () => {
            window.visualViewport?.removeEventListener(
                "resize",
                handleViewportChange
            );
            window.visualViewport?.removeEventListener(
                "scroll",
                handleViewportChange
            );
        };
    }, []);

    return {
        visualViewportScale,
    };
}
